import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { useAuth } from '@/auth/use-auth';
import { FormTextField } from '@/components/form/form-text-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AuthCard } from '@/pages/auth/auth-card';
import { applyApiErrors } from '@/pages/auth/form-errors';
import type { LoginLocationState } from '@/pages/auth/login-state';
import { TwoFactorChallenge } from '@/pages/auth/two-factor-challenge';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  remember: z.boolean().optional(),
});

type LoginValues = z.infer<typeof schema>;

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [challenge, setChallenge] = useState(false);

  const state = location.state as LoginLocationState | null;
  const from = state?.from ?? '/';
  const notice = state?.notice;

  const form = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await login({
        email: values.email,
        password: values.password,
        remember: values.remember,
      });
      if (result.kind === 'two_factor_required') {
        setChallenge(true);
        return;
      }
      toast.success(t('login.success'));
      navigate(from, { replace: true });
    } catch (error) {
      applyApiErrors(error, form.setError, (message) => toast.error(message));
    }
  });

  if (challenge) {
    return (
      <TwoFactorChallenge
        onVerified={() => {
          toast.success(t('login.success'));
          navigate(from, { replace: true });
        }}
        onCancel={() => setChallenge(false)}
      />
    );
  }

  return (
    <AuthCard
      title={t('login.title')}
      footer={
        <div className="flex flex-col gap-1">
          <span>
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-green">
              {t('register.title')}
            </Link>
          </span>
          <Link to="/forgot" className="text-dim hover:text-strong">
            {t('login.forgot')}
          </Link>
        </div>
      }
    >
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {notice === 'passwordReset' ? (
            <p className="font-mono text-sm text-green" role="status">
              {t('resetPassword.success')}
            </p>
          ) : null}
          <FormTextField
            control={form.control}
            name="email"
            label={t('fields.email')}
            type="email"
            autoComplete="email"
          />
          <FormTextField
            control={form.control}
            name="password"
            label={t('fields.password')}
            type="password"
            autoComplete="current-password"
          />
          <label className="flex items-center gap-2 font-mono text-xs text-dim">
            <input
              type="checkbox"
              className="accent-[color:var(--orange)]"
              {...form.register('remember')}
            />
            {t('login.remember')}
          </label>
          <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
            {t('login.submit')}
          </Button>
        </form>
      </Form>
    </AuthCard>
  );
}
